// 共享服务 - 适配实验室管理系统的API调用方式
const API_BASE = '/api';

// 辅助函数：获取token
const getToken = () => {
  return localStorage.getItem('token');
};

// 辅助函数：API请求
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const error = await response.json().catch(() => ({ message: '请求失败' }));
    throw new Error(error.message || '请求失败');
  }

  return response.json();
};

export interface SharedFolderPermissions {
  canEditNotes: boolean;
  canEditLiterature: boolean;
  canAddLiterature: boolean;
  canRemoveLiterature: boolean;
  canViewUsers: boolean;
}

export interface SharedFolder {
  _id: string;
  name: string;
  description?: string;
  owner: {
    _id: string;
    username: string;
    email: string;
  };
  sharedUsers: Array<{
    user: {
      _id: string;
      username: string;
      email: string;
    };
    email: string;
    sharedAt: string;
    status?: 'pending' | 'joined';
  }>;
  isPublic: boolean;
  permissions?: SharedFolderPermissions;
  createdAt: string;
  updatedAt: string;
  isOwner?: boolean;
}

export interface CreateSharedFolderData {
  name: string;
  description?: string;
}

export interface UpdateSharedFolderData {
  name?: string;
  description?: string;
  permissions?: SharedFolderPermissions;
}

export const sharedService = {
  // 获取用户的共享文件夹列表
  getMyFolders: async (): Promise<SharedFolder[]> => {
    const response = await apiRequest('/shared/folders');
    return response.data.folders;
  },

  // 获取被共享给我的文件夹列表
  getSharedWithMeFolders: async (): Promise<SharedFolder[]> => {
    const response = await apiRequest('/shared/folders/shared-with-me');
    return response.data.folders;
  },

  // 创建共享文件夹
  createFolder: async (data: CreateSharedFolderData): Promise<SharedFolder> => {
    const response = await apiRequest('/shared/folders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data.folder;
  },

  // 更新共享文件夹
  updateFolder: async (id: string, data: UpdateSharedFolderData): Promise<SharedFolder> => {
    const response = await apiRequest(`/shared/folders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data.folder;
  },

  // 删除共享文件夹
  deleteFolder: async (id: string): Promise<void> => {
    await apiRequest(`/shared/folders/${id}`, { method: 'DELETE' });
  },

  // 发送共享邀请（通过邮箱，兼容旧调用）
  inviteUser: async (folderId: string, email: string, message?: string) => {
    const response = await apiRequest(`/shared/folders/${folderId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, message }),
    });
    return response;
  },

  // 批量邀请团队成员（按用户 ID）
  inviteUsers: async (folderId: string, userIds: string[], message?: string) => {
    const response = await apiRequest(`/shared/folders/${folderId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ userIds, message }),
    });
    return response;
  },

  // 添加共享用户（通过邮箱）- 直接添加
  addSharedUser: async (folderId: string, email: string): Promise<SharedFolder> => {
    const response = await apiRequest(`/shared/folders/${folderId}/users`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response.data.folder;
  },

  // 删除共享用户
  removeSharedUser: async (folderId: string, userId: string): Promise<SharedFolder> => {
    const response = await apiRequest(`/shared/folders/${folderId}/users/${userId}`, {
      method: 'DELETE',
    });
    return response.data.folder;
  },

  // 获取共享文件夹中的文献列表
  getFolderLiteratures: async (folderId: string) => {
    const response = await apiRequest(`/shared/folders/${folderId}/literatures`);
    return response.data;
  },

  // 添加文献到共享文件夹
  addLiteraturesToFolder: async (folderId: string, literatureIds: string[], syncNotes: boolean = false) => {
    const response = await apiRequest(`/shared/folders/${folderId}/literatures`, {
      method: 'POST',
      body: JSON.stringify({ literatureIds, syncNotes }),
    });
    return response.data;
  },

  // 从共享文件夹中移除文献
  removeLiteratureFromFolder: async (folderId: string, literatureId: string): Promise<void> => {
    await apiRequest(`/shared/folders/${folderId}/literatures/${literatureId}`, { method: 'DELETE' });
  }
};
